/**
 * CostSplitGroups — operator panel for bulk-purchase cost splitting.
 *
 * Some jobs are the same scope built twice (Kal Tire "Tire Sight x Fuel Cell
 * Integration" #2621 + #2622, one address, two contracts) and their material is
 * bought in ONE bulk order under ONE PO. Left alone that invoice lands whole on
 * the PO's job, so one job looks double on material and the other free.
 *
 * Declaring the two jobs as a GROUP makes every expense that arrives on either
 * job's PO split automatically — qty and dollars — into one Jobber expense per
 * job. This panel creates groups, previews/applies the split for purchases that
 * were already booked whole (backfill), and runs the audit.
 *
 * All work happens server-side in /api/expense-split (admin-gated); this file
 * never touches job_cost_split* directly, which is why those tables stay
 * admin-only in security/dataPolicy.js.
 */
import { useState, useCallback } from "react";
import { apiFetch } from "../lib/apiFetch";

const C = {
  surface: "#13161b",
  border: "#1e2329",
  text: "#e8ecf0",
  muted: "#8a919c",
  green: "#22c55e",
  red: "#ef4444",
  yellow: "#f59e0b",
  blue: "#60a5fa",
  purple: "#c084fc",
};

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const btn = (active, color = C.blue) => ({
  padding: "5px 10px",
  borderRadius: 6,
  border: `1px solid ${active ? color : C.border}`,
  background: active ? "rgba(96,165,250,0.1)" : "transparent",
  color: active ? color : C.muted,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
});

export default function CostSplitGroups() {
  const [groups, setGroups] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(null);       // action key currently running
  const [result, setResult] = useState(null);   // last backfill/audit/split payload
  const [newJobs, setNewJobs] = useState("");
  const [newName, setNewName] = useState("");

  const call = useCallback(async (body) => {
    const r = await apiFetch("/api/expense-split", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setMsg(null);
    try {
      const d = await call({ action: "groups" });
      setGroups(d.groups || []);
    } catch (e) {
      setError(e.message || "Failed to load split groups");
      setGroups(null);
    } finally {
      setLoading(false);
    }
  }, [call]);

  async function run(key, body, describe) {
    setBusy(key); setError(null); setMsg(null); setResult(null);
    try {
      const d = await call(body);
      setResult({ key, data: d });
      if (d.ok === false) setError(d.error || "Action reported a problem");
      else setMsg(describe(d));
      if (body.action !== "audit" && body.action !== "preview") await load();
    } catch (e) {
      setError(e.message || "Action failed");
    } finally {
      setBusy(null);
    }
  }

  async function createGroup() {
    const jobNumbers = newJobs.split(/[^0-9]+/).filter(Boolean);
    if (jobNumbers.length < 2) { setError("Enter at least two job numbers, e.g. 2621, 2622"); return; }
    await run("create", { action: "create-group", jobNumbers, name: newName || undefined }, (d) =>
      d.warning
        ? `Group created for ${jobNumbers.map((n) => `#${n}`).join(" + ")} — ${d.warning}`
        : `Group created: ${jobNumbers.map((n) => `#${n}`).join(" + ")} will now split every bulk purchase evenly.`
    );
    setNewJobs(""); setNewName("");
  }

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em" }}>
          Bulk-Purchase Cost Split
        </div>
        <button onClick={load} disabled={loading} style={btn(true)}>
          {loading ? "Loading…" : groups ? "Refresh" : "Load groups"}
        </button>
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 14 }}>
          A <strong style={{ color: C.text }}>split group</strong> is a set of jobs that are the same scope built
          twice and share bulk material orders. Any expense arriving on a member job's PO is divided evenly —
          every line's qty and every dollar — into one Jobber expense per job, tagged{" "}
          <span style={{ color: C.purple }}>⇄ SPLIT</span>.{" "}
          <strong style={{ color: C.text }}>QuickBooks still gets one Bill for the full amount</strong>, because that
          is the real payable to the supplier; only the job costing in Jobber is split.
        </div>

        {/* Create */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", paddingBottom: 14, borderBottom: `1px solid ${C.border}` }}>
          <input
            value={newJobs}
            onChange={(e) => setNewJobs(e.target.value)}
            placeholder="Job numbers, e.g. 2621, 2622"
            style={{ flex: "1 1 200px", padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "#0d0f12", color: C.text, fontSize: 13 }}
          />
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Name (optional)"
            style={{ flex: "1 1 180px", padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "#0d0f12", color: C.text, fontSize: 13 }}
          />
          <button onClick={createGroup} disabled={busy === "create"} style={btn(true, C.green)}>
            {busy === "create" ? "Creating…" : "Create group"}
          </button>
        </div>

        {error && (
          <div style={{ marginTop: 12, padding: "8px 10px", borderRadius: 6, background: "rgba(239,68,68,0.08)", border: `1px solid ${C.red}`, color: C.red, fontSize: 12 }}>
            {error}
          </div>
        )}
        {msg && (
          <div style={{ marginTop: 12, padding: "8px 10px", borderRadius: 6, background: "rgba(34,197,94,0.08)", border: `1px solid ${C.green}`, color: C.green, fontSize: 12 }}>
            {msg}
          </div>
        )}

        {/* Groups */}
        {groups && groups.length === 0 && (
          <div style={{ marginTop: 14, fontSize: 12, color: C.muted }}>
            No split groups yet. Add the two job numbers above to create one.
          </div>
        )}

        {(groups || []).map((g) => (
          <div key={g.id} style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                  {g.name || g.jobNumbers.map((n) => `#${n}`).join(" + ")}
                  {!g.active && <span style={{ marginLeft: 8, fontSize: 11, color: C.muted }}>(inactive)</span>}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                  {g.weights ? "weighted split" : "even split"} · {g.parentExpenses} purchase(s) split ·{" "}
                  auto-split {g.autoSplit ? "ON" : "OFF"}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button onClick={() => run(`bf-${g.id}`, { action: "backfill", groupId: g.id }, (d) => `Dry run: ${d.wouldSplit} purchase(s) would be split. Nothing written yet.`)}
                  disabled={!!busy} style={btn(true, C.yellow)}>
                  {busy === `bf-${g.id}` ? "Checking…" : "Backfill (dry run)"}
                </button>
                <button onClick={() => run(`bfa-${g.id}`, { action: "backfill", groupId: g.id, apply: true }, (d) => `Split ${d.split || 0} purchase(s); ${d.failed || 0} failed.`)}
                  disabled={!!busy} style={btn(true, C.green)}>
                  {busy === `bfa-${g.id}` ? "Splitting…" : "Apply backfill"}
                </button>
                <button onClick={() => run(`au-${g.id}`, { action: "audit", groupId: g.id }, (d) => `Audit: ${d.verdict} — ${d.highCount} high, ${d.warnCount} warn.`)}
                  disabled={!!busy} style={btn(true)}>
                  {busy === `au-${g.id}` ? "Auditing…" : "Audit"}
                </button>
                <button onClick={() => run(`ac-${g.id}`, { action: "update-group", groupId: g.id, active: !g.active }, () => g.active ? "Group paused." : "Group re-enabled.")}
                  disabled={!!busy} style={btn(false)}>
                  {g.active ? "Pause" : "Enable"}
                </button>
              </div>
            </div>

            {g.unresolvedMembers?.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 11, color: C.yellow }}>
                ⚠ Job {g.unresolvedMembers.map((n) => `#${n}`).join(", ")} isn't in the Jobber job cache — auto-split
                will skip this group until a Jobber sync runs.
              </div>
            )}

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
              {(g.perJob || []).map((j) => (
                <div key={j.jobNumber} style={{ flex: "1 1 180px", background: "#0d0f12", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Job #{j.jobNumber}</div>
                  {j.title && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{j.title}</div>}
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.purple, marginTop: 6 }}>{money(j.total)}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {j.shares} split share(s){j.failed > 0 && <span style={{ color: C.red }}> · {j.failed} failed</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Last action detail */}
        {result?.data && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
              Last action
            </div>

            {/* Audit findings, most expensive first */}
            {Array.isArray(result.data.report) && result.data.report.map((rep) => (
              <div key={rep.group.id} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: rep.verdict === "CLEAN" ? C.green : rep.verdict === "REVIEW" ? C.yellow : C.red, fontWeight: 600 }}>
                  {rep.group.jobNumbers.map((n) => `#${n}`).join(" + ")} — {rep.verdict}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {rep.splitExpenses} purchase(s) split, {money(rep.totalSplit)} total ·{" "}
                  {Object.entries(rep.splitMaterialPerJob || {}).map(([j, v]) => `#${j} ${money(v)}`).join(" vs ")}
                </div>
                {(rep.findings || []).map((f, i) => (
                  <div key={i} style={{ marginTop: 6, fontSize: 11, color: f.severity === "high" ? C.red : C.yellow, lineHeight: 1.5 }}>
                    {f.severity === "high" ? "🔴" : "🟡"} <strong>{f.check}</strong> {money(f.amount)} — {f.detail}
                  </div>
                ))}
              </div>
            ))}

            {/* Backfill dry-run plan */}
            {Array.isArray(result.data.plan) && result.data.plan.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ color: C.muted, textAlign: "left" }}>
                      <th style={{ padding: "4px 6px" }}>Supplier</th>
                      <th style={{ padding: "4px 6px" }}>Invoice</th>
                      <th style={{ padding: "4px 6px" }}>Total</th>
                      <th style={{ padding: "4px 6px" }}>Shares</th>
                      <th style={{ padding: "4px 6px" }}>What would happen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.data.plan.map((p) => (
                      <tr key={p.invoiceId} style={{ borderTop: `1px solid ${C.border}`, color: C.text }}>
                        <td style={{ padding: "4px 6px" }}>{p.supplier || "—"}</td>
                        <td style={{ padding: "4px 6px" }}>{p.invoice_number || "—"}</td>
                        <td style={{ padding: "4px 6px" }}>{money(p.total)}</td>
                        <td style={{ padding: "4px 6px", color: C.purple }}>
                          {(p.shares || []).map((s) => `#${s.job} ${money(s.total)}`).join(" · ")}
                        </td>
                        <td style={{ padding: "4px 6px", color: C.muted }}>{p.action}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Tech-entered Jobber expenses we deliberately did NOT auto-split */}
            {result.data.jobberNativeExpensesNotFromElecBid && (
              <div style={{ marginTop: 10, fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
                <strong style={{ color: C.text }}>Tech-entered Jobber expenses on these jobs (NOT auto-split)</strong> — stock,
                dump runs, fuel. Split individually if they were bought for both jobs:
                {Object.entries(result.data.jobberNativeExpensesNotFromElecBid).map(([job, list]) => (
                  <div key={job} style={{ marginTop: 4 }}>
                    #{job}: {Array.isArray(list)
                      ? (list.length ? list.map((e) => `${e.title} ${money(e.total)}`).join(" · ") : "none")
                      : `error — ${list?.error || "unknown"}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
